import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { useIntl } from 'react-intl';
import { useCreateMasterStreamLinkMutation } from '../../../../../redux/api/fymAPI';
import { useShowToast } from '../../../../../redux/slices/toasts/hooks';
import { masterAudio, MasterAudioStore, MasterPlayerStatus } from './masterAudio';
import { masterPlayerMessages } from './masterPlayerMessages';

interface UseMasterPlayerProps {
  accountId: number;
  itemUuid: string;
  /**
   * A public audio link to play in place of `POST .../master/stream` (the Studio showcase plays a
   * fixture copy and has no account). With it, the hook never calls the API.
   */
  streamUrl?: string;
  /** For tests. The app has one store. */
  store?: MasterAudioStore;
}

export interface MasterPlayerControls {
  status: MasterPlayerStatus;
  positionSec: number;
  /** The length the audio reports. Null before the first play. */
  durationSec: number | null;
  toggle: () => void;
  /** Seeks the song. A song that does not play starts at that second. */
  seek: (seconds: number) => void;
  /** Plays the song from `seconds`: a paused or stopped song starts there, a playing song jumps there. */
  playFrom: (seconds: number) => void;
  /** The exact play position now (not the last rendered one), or null when another song holds the player. */
  livePositionSec: () => number | null;
}

/**
 * The player of one catalog row (design release-kit o). Every view of the row reads the one
 * shared store, so a play in the song files sheet pauses the release page, and the reverse. The
 * stream link comes from `POST .../master/stream` only when the user presses Play. The song stops
 * when the last view of the row unmounts.
 */
export function useMasterPlayer({
  accountId,
  itemUuid,
  streamUrl,
  store = masterAudio,
}: UseMasterPlayerProps): MasterPlayerControls {
  const intl = useIntl();
  const { showError } = useShowToast();
  const state = useSyncExternalStore(store.subscribe, store.getState);
  const [createLink] = useCreateMasterStreamLinkMutation();

  useEffect(() => {
    store.retain(itemUuid);
    return () => store.release(itemUuid);
  }, [itemUuid, store]);

  const loader = useCallback(
    async () => streamUrl ?? (await createLink({ accountId, itemUuid }).unwrap()).url,
    [accountId, createLink, itemUuid, streamUrl]
  );

  const mine = state.itemUuid === itemUuid;
  const status: MasterPlayerStatus = mine ? state.status : 'idle';

  // One toast per failed play.
  const shownError = useRef(false);
  useEffect(() => {
    if (status === 'error' && !shownError.current) {
      shownError.current = true;
      showError(intl.formatMessage(masterPlayerMessages.playFailed));
    } else if (status !== 'error') {
      shownError.current = false;
    }
  }, [intl, showError, status]);

  const toggle = useCallback(() => {
    if (mine && (status === 'playing' || status === 'loading')) {
      store.pause();
      return;
    }
    store.play(itemUuid, loader).catch(console.error);
  }, [itemUuid, loader, mine, status, store]);

  const seek = useCallback(
    (seconds: number) => {
      if (mine && status !== 'error' && status !== 'idle') {
        store.seek(itemUuid, seconds);
        return;
      }
      store.play(itemUuid, loader, seconds).catch(console.error);
    },
    [itemUuid, loader, mine, status, store]
  );

  // The lyric sync editor (design release-kit s8): ▶ on a line plays from just before it.
  const playFrom = useCallback(
    (seconds: number) => {
      const at = Math.max(0, seconds);
      if (mine && (status === 'playing' || status === 'loading')) {
        store.seek(itemUuid, at);
        return;
      }
      // A paused song starts at `at`; a stopped song loads its link and starts at `at`.
      store.play(itemUuid, loader, at).catch(console.error);
    },
    [itemUuid, loader, mine, status, store]
  );

  const livePositionSec = useCallback(() => store.livePositionSec(itemUuid), [itemUuid, store]);

  return {
    status,
    positionSec: mine ? state.positionSec : 0,
    durationSec: mine ? state.durationSec : null,
    toggle,
    seek,
    playFrom,
    livePositionSec,
  };
}
